import { useState, useCallback, useMemo, useEffect } from "react";
import { TimeBlockStatus } from "../../../entities-generated/common/time-block-status";
import { ITimeBlockStatusOption } from "../../../entities-generated/common/time-block-status-option";
import { IBwjFormOption } from "../../../framework/entities/bwj-form-option";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ValidatedFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged } from "../../../framework/utils";
import { none, where } from "../../../framework/utils/collection";
import { TimeBlockEditorModel } from "../entities/time-block-editor-model";
import { TimeBlockEvents } from "../events";
import { ITimeBlocksEditorFormModel, ITimeBlocksEditorValueModel, ITimeBlocksEditorModelChange } from "./blocks-editor-iface";
import { IChangeTimeBlocksRequest } from "../../../entities-generated/models/time-block/change-time-blocks-request";

// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:status/validated/ITimeBlockStatusOption | null/TimeBlockStatus%%
export const status = new (class statusService extends ValidatedFormValueService<TimeBlockEditorModel, ITimeBlockStatusOption | null, TimeBlockStatus> {
    public initialize(data: TimeBlockEditorModel): void {
        this.options = data.statusOptions;
        this.setInputValue(new BwjInput<ITimeBlockStatusOption | null>(null));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: TimeBlockEditorModel): void {
        data.statusOptions = this.options;
    }

    public getDefaultValue(): BwjInput<ITimeBlockStatusOption | null> {
        return new BwjInput<ITimeBlockStatusOption | null>(null);
    }

    public getInputValue(): BwjInput<ITimeBlockStatusOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<ITimeBlockStatusOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): TimeBlockStatus {
        const value = this.value.value;
        return value?.id ?? TimeBlockStatus.Available;
    }

    public validate(value: ITimeBlockStatusOption | null): Promise<BwjInput<ITimeBlockStatusOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a status');
        return Promise.resolve(result);
    }

    public onUpdate(value: ITimeBlockStatusOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }
    
    public options: ITimeBlockStatusOption[] = [];
})(
    'status' // id
    ,'Change blocks to status' // description
);
// %%END:status/validated/ITimeBlockStatusOption | null/TimeBlockStatus%%

// %%BEGIN:isBillableTime/validated/IBwjFormOption<string> | null/boolean | null%%
export const isBillableTime = new (class isBillableTimeService extends ValidatedFormValueService<TimeBlockEditorModel, IBwjFormOption<string> | null, boolean | null> {
    public initialize(data: TimeBlockEditorModel): void {
        this.setInputValue(new BwjInput<IBwjFormOption<string> | null>(this.options[0]));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: TimeBlockEditorModel): void {}

    public getDefaultValue(): BwjInput<IBwjFormOption<string> | null> {
        return new BwjInput<IBwjFormOption<string> | null>(null);
    }

    public getInputValue(): BwjInput<IBwjFormOption<string> | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IBwjFormOption<string> | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): boolean | null {
        const value = this.value.value;
        if(value === null) { return null; }
        return value.value === 'true' ? true
            : value.value === 'false' ? false : null;
    }

    public validate(value: IBwjFormOption<string> | null): Promise<BwjInput<IBwjFormOption<string> | null>> {
        const result = this.requiredFieldValidator(value, 'Select an option');
        return Promise.resolve(result);
    }

    public onUpdate(value: IBwjFormOption<string> | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IBwjFormOption<string>[] = [{
        value: 'null',
        description: 'Leave unchanged'
    }, {
        value: 'true',
        description: 'Change to billable time'
    }, {
        value: 'false',
        description: 'Change to non-billable time'
    }];

})(
    'isBillableTime' // id
    ,'Time block type' // description
);
// %%END:isBillableTime/validated/IBwjFormOption<string> | null/boolean | null%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): ITimeBlocksEditorFormModel => {
    return {
       status: status.getInputValue(),
       isBillableTime: isBillableTime.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): ITimeBlocksEditorValueModel => {
    return {
       status: status.getOutputValue(),
       isBillableTime: isBillableTime.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        status.getInputValue(),
        isBillableTime.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: ITimeBlocksEditorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(TimeBlockEvents.EditFormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<ITimeBlocksEditorFormModel>> => {
    const promises = [
        status.formValidateAndUpdate(),
        isBillableTime.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:ITimeBlocksEditorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: ITimeBlocksEditorValueModel, dataModel: TimeBlockEditorModel } = {
    dataModel: new TimeBlockEditorModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: TimeBlockEditorModel) => {
    const dataModel = data || new TimeBlockEditorModel();

    status.initialize(dataModel);
    isBillableTime.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:ITimeBlocksEditorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
status.setChangePublisher(broadcastChange);
isBillableTime.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new TimeBlockEditorModel();

    status.applyToDataModel(dataModel);
    isBillableTime.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: ITimeBlocksEditorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], TimeBlockEvents.EditFormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): ITimeBlocksEditorFormModel => {
    const [getter, setter] = useState<ITimeBlocksEditorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: ITimeBlocksEditorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: ITimeBlocksEditorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('TimeBlocksEditorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('TimeBlocksEditorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toChangeRequest = (startBlock: number, endBlock: number) => {
    const v = toValueModel();
    const request: IChangeTimeBlocksRequest = {
        status: v.status,
        isBillableTime: v.isBillableTime,
        startBlock,
        endBlock
    };
    return request;
};

// ##END:DEVELOPERCODE##
